import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const Sidebar = ({ isCollapsed = false, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const navigationItems = [
    {
      label: 'Main Dashboard',
      path: '/main-dashboard',
      icon: 'LayoutDashboard',
      locked: false,
      tooltip: 'Your command center'
    },
    {
      label: 'Start Here',
      path: '/start-here-hub',
      icon: 'Rocket',
      locked: false,
      tooltip: 'Begin your onboarding journey'
    },
    {
      label: 'Role Playbook',
      path: '/role-playbook-hub',
      icon: 'BookOpen',
      locked: true,
      tooltip: 'Coming soon: Role-specific knowledge base'
    },
    {
      label: 'Culture & Rituals',
      path: '/culture-and-rituals-hub',
      icon: 'Users',
      locked: true,
      tooltip: 'Coming soon: Company values and traditions'
    },
    {
      label: 'Tools & Access',
      path: '/tools-and-access-hub',
      icon: 'Wrench',
      locked: true,
      tooltip: 'Coming soon: Application integrations'
    }
  ];

  const handleNavigation = (path, locked) => {
    if (!locked) {
      navigate(path);
      setIsMobileOpen(false);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  return (
    <>
      <button
        onClick={toggleMobileMenu}
        className="fixed top-4 left-4 z-50 lg:hidden bg-card p-2 rounded-lg shadow-elevation-2 hover:bg-muted transition-colors duration-150"
        aria-label="Toggle navigation menu"
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={24} color="var(--color-foreground)" />
      </button>
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden animate-fade-in"
          onClick={toggleMobileMenu}
          aria-hidden="true"
        />
      )}
      <aside
        className={`fixed lg:fixed top-0 left-0 h-full bg-card border-r border-border z-50 transition-all duration-300 ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${isCollapsed ? 'lg:w-20' : 'lg:w-60'} w-60 shadow-elevation-2 animate-slide-in-left`}
      >
        <div className="sidebar-header flex items-center justify-center h-20 border-b border-border bg-secondary/20 backdrop-blur-sm">
          <div className={`sidebar-logo flex items-center justify-center transition-all duration-300 ${
            isCollapsed ? 'w-10 h-10' : 'w-12 h-12'
          } bg-primary/10 rounded-lg`}>
            <Icon 
              name="Hexagon" 
              size={isCollapsed ? 24 : 32} 
              color="var(--color-primary)" 
              strokeWidth={2}
            />
          </div>
          {!isCollapsed && (
            <span className="ml-3 text-xl font-bold text-primary font-sans">Bee Wise</span>
          )}
        </div>

        <nav className="p-4 space-y-2" role="navigation" aria-label="Main navigation">
          {navigationItems?.map((item) => {
            const isActive = location?.pathname === item?.path;
            const itemClasses = `
              flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
              ${isActive ? 'bg-primary/10 text-primary shadow-glow' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}
              ${item?.locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-102'}
              ${isCollapsed ? 'justify-center' : ''}
            `;

            return (
              <div
                key={item?.path}
                onClick={() => handleNavigation(item?.path, item?.locked)}
                className={itemClasses}
                role="button"
                tabIndex={item?.locked ? -1 : 0}
                aria-label={item?.label}
                aria-disabled={item?.locked}
                title={item?.tooltip}
                onKeyDown={(e) => {
                  if (e?.key === 'Enter' || e?.key === ' ') {
                    e?.preventDefault();
                    handleNavigation(item?.path, item?.locked);
                  }
                }}
              >
                <Icon 
                  name={item?.icon} 
                  size={20} 
                  color={isActive ? 'var(--color-primary)' : 'currentColor'}
                  strokeWidth={isActive ? 2.5 : 2}
                />
                {!isCollapsed && (
                  <span className="font-medium text-sm flex-1">{item?.label}</span>
                )}
                {!isCollapsed && item?.locked && (
                  <Icon name="Lock" size={16} color="currentColor" />
                )}
              </div>
            );
          })}
        </nav>

        <div className="absolute bottom-4 left-0 right-0 px-4">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-lg bg-muted/30 ${isCollapsed ? 'justify-center' : ''}`}>
            <Icon name="HelpCircle" size={20} color="var(--color-muted-foreground)" />
            {!isCollapsed && (
              <span className="text-sm text-muted-foreground">Need help?</span>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;